import { useT } from "../i18n";

/**
 * Logo is the SageLeaf mark: five bars whose envelope is a leaf, leaning right.
 *
 * The lean and the sage-to-sky ramp both come from the original SageLeaf icon —
 * a tilted green leaf with glowing blue veins. Here the veins have become the
 * thing itself, because this product's subject is narration rather than
 * annotation.
 *
 * The gradient is declared with `gradientUnits="userSpaceOnUse"`. The default,
 * objectBoundingBox, resolves against each child's own box when the fill is set
 * on the group — giving five bars that each run the whole ramp, which reads as
 * flat green.
 */
export default function Logo({
  size = 24,
  className,
  /** Draw in the surrounding text colour instead of brand colours. */
  monochrome = false,
  /** Hidden from screen readers, where the name is already said beside it. */
  decorative = false,
}: {
  size?: number;
  className?: string;
  monochrome?: boolean;
  decorative?: boolean;
}) {
  // Unique per instance: two inline SVGs sharing a gradient id would have the
  // second one silently reference the first's definition.
  const id = `sl-${monochrome ? "mono" : "brand"}-${size}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      className={className}
      {...(decorative ? { "aria-hidden": true } : { role: "img", "aria-label": "SageLeaf" })}
    >
      {!monochrome && (
        <defs>
          <linearGradient id={id} gradientUnits="userSpaceOnUse" x1="4" y1="27" x2="29" y2="6">
            <stop offset="0" stopColor="#4A7A2A" />
            <stop offset="0.4" stopColor="#6B9D3E" />
            <stop offset="1" stopColor="#7FC5E0" />
          </linearGradient>
        </defs>
      )}
      <g transform="rotate(-20 16 16)" fill={monochrome ? "currentColor" : `url(#${id})`}>
        <rect x="3.5" y="11.5" width="3.4" height="9" rx="1.7" />
        <rect x="9.3" y="7.5" width="3.4" height="17" rx="1.7" />
        <rect x="15.1" y="4.5" width="3.4" height="23" rx="1.7" />
        <rect x="20.9" y="7.5" width="3.4" height="17" rx="1.7" />
        <rect x="26.7" y="11.5" width="3.4" height="9" rx="1.7" />
      </g>
    </svg>
  );
}

const LOCKUP = {
  // The word's size; the mark stands a third taller, so its tallest bar rises
  // over the capitals, as in the app's BrandLockup (mobile/lib/ui/brand_mark.dart).
  sm: { mark: 18, text: "text-[0.85rem]" },
  md: { mark: 26, text: "text-[1.25rem]" },
  lg: { mark: 46, text: "text-[2.1rem]" },
} as const;

/**
 * Lockup is the name as it is signed: the mark and "SageLeaf" in the reading
 * serif, one unit at three sizes. It stands in the header, the landing's hero
 * and the footer — never in the reader, the player or the Ask sheet (owner,
 * 10-24: "as if we're not proud of it"). The word is sage, or the surrounding
 * text colour with `plain` (the hero's candle-lit cream).
 */
export function Lockup({
  size = "md",
  plain = false,
  className = "",
}: {
  size?: keyof typeof LOCKUP;
  plain?: boolean;
  className?: string;
}) {
  const t = useT();
  const { mark, text } = LOCKUP[size];
  return (
    <span className={`inline-flex items-center gap-[0.3em] ${text} ${className}`}>
      <Logo size={mark} className="shrink-0" decorative />
      <span
        className={`font-brand leading-none font-bold tracking-[-0.01em] ${plain ? "" : "text-sage-deep"}`}
      >
        {t.nav.brand}
      </span>
    </span>
  );
}
